import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import checkoutOrderModelPriceService from "../checkout/orderItems/CheckoutOrderModelPriceService";
import { BasketPageCheckoutOrderModel } from "./checkoutOrderView";
import BasketFreeShippingNote from "./BasketFreeShippingNote";
import BasketListItem from "./BasketListItem";
import BasketUsedDiscountCode from "./BasketUsedDiscountCode";
import BasketDiscountCodeInputBlock from "./BasketDiscountCodeInputBlock";
import BasketTotalAmountBlock from "./BasketTotalAmountBlock";
import EmptyBasketList from "./EmptyBasketList";
import BasketListAccessory from "./BasketListAccessory";
import OfferEshopAccessoriesBlock from "./OfferEshopAccessoriesBlock";
import SharedBasketButton from "./SharedBasketButton";

interface BasketListItemsProperties {
    readonly basketPageCheckoutOrderModel: BasketPageCheckoutOrderModel;
}

export default function BasketListItems({ basketPageCheckoutOrderModel }: BasketListItemsProperties): React.JSX.Element {
    const { t } = useTranslation("basket");
    const [model, setModel] = useState<BasketPageCheckoutOrderModel>(basketPageCheckoutOrderModel);

    const itemPriceSummary = checkoutOrderModelPriceService.getPriceSummary(model.checkoutOrderView);

    const handleModelChange = (newModel: BasketPageCheckoutOrderModel): void => setModel(newModel);

    const basketItems = [];

    for (const item of model.checkoutOrderView.items) {
        basketItems.push(<BasketListItem item={item} key={item.productId} onModelChange={handleModelChange} currency={basketPageCheckoutOrderModel.checkoutOrderView.currency} />);
    }

    const mandatoryOrderAccessories = [];

    for (const orderAccessory of model.checkoutOrderView.orderAccessories.filter((accessory) => !accessory.canModify)) {
        mandatoryOrderAccessories.push(<BasketListAccessory
            orderAccessory={orderAccessory}
            key={orderAccessory.eshopAccessoryId}
            currency={basketPageCheckoutOrderModel.checkoutOrderView.currency}
        />);
    }

    return (
        model.checkoutOrderView.items.length === 0
            ? <EmptyBasketList />
            : <>

                {model.freeShippingMinimalOrderPrice !== null && <BasketFreeShippingNote
                    itemsCount={model.checkoutOrderView.items.length}
                    freeShippingMinimalOrderPrice={model.freeShippingMinimalOrderPrice}
                    itemsAmount={itemPriceSummary.itemsAndAccessoriesAmount}
                    currency={basketPageCheckoutOrderModel.checkoutOrderView.currency}
                />}

                {new Date() <= new Date(2025, 9, 19) && (
                    <div className="alert alert-success cart-step1-alert red-alert">
                        <div className="watchdog-new-1-top">
                            <div className="watchdog-new-1-data">
                                <i className="fa-solid fa-circle-exclamation"></i>
                                <span><strong>{t("movingDate")}</strong>{t("delayPossible")}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="col-sm-12 new-step1-container">
                    <div className="row">
                        <div className="col-sm-9 new-step1-container">
                            <div className="clearfix">
                                <SharedBasketButton />
                                <h1><i className="fa-solid fa-shopping-basket"></i> {t("basketPageTitle")}</h1>
                                <span>{t("itemsNotReserved")}</span>
                            </div>
                        </div>
                        <div className="col-sm-3 new-step1-container"></div>
                    </div>
                </div>

                <div className="col-sm-12 new-step1-container">

                    <div className="row cart-step1-container">
                        <div className="col-sm-9">
                            {basketItems}

                            {model.checkoutOrderView.insertedEshopDiscount !== null && <BasketUsedDiscountCode
                                itemsAmount={itemPriceSummary.itemsAmount}
                                itemsAmountWithoutDiscount={itemPriceSummary.itemsAmountWithoutDiscount}
                                eshopDiscount={model.checkoutOrderView.insertedEshopDiscount}
                                isApplied={model.checkoutOrderView.eshopDiscount !== null}
                                onModelChange={handleModelChange}
                                currency={basketPageCheckoutOrderModel.checkoutOrderView.currency}
                            />}

                            {mandatoryOrderAccessories}

                            <BasketDiscountCodeInputBlock
                                onModelChange={handleModelChange}
                                discountCodeAlreadyInserted={model.checkoutOrderView.eshopDiscount !== null}
                            />

                            <OfferEshopAccessoriesBlock
                                eshopAccessories={basketPageCheckoutOrderModel.eshopAccessories}
                                orderAccessories={model.checkoutOrderView.orderAccessories}
                                onModelChange={handleModelChange}
                                currency={basketPageCheckoutOrderModel.checkoutOrderView.currency}
                            />
                        </div>

                        <BasketTotalAmountBlock
                            itemsAmount={itemPriceSummary.itemsAndAccessoriesAmount}
                            items={model.checkoutOrderView.items}
                            checkoutUrl={model.checkoutUrl}
                            currency={basketPageCheckoutOrderModel.checkoutOrderView.currency}
                        />
                    </div>
                </div>
            </>
    );
}
